import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch as useReduxDispatch } from 'react-redux';
import { clearCart } from '../../../features/cart/cartSlice';
import { useCustomerAuth } from '../../../contexts/CustomerAuthContext';
import { STEPS } from '../CheckoutModal';
import { apiFetch } from '../../../utils/api';
import CustomerFacingSubtotal from '../../checkout/CustomerFacingSubtotal';
import { getQuoteTaxInfo } from '../../../utils/merchantTax';

function buildCheckoutRequest({ state, storeSlug, cartItems, cartTotal, shippingFee, hasPhysicalProducts }) {
  // Map UI method → backend paymentMethod enum. Manual Transfer maps
  // to the existing BANK_TRANSFER PaymentMethodType enum value so we
  // don't need a schema change; the checkout endpoint short-circuits
  // gateway initialization for BANK_TRANSFER.
  const backendPaymentMethod = state.paymentMethod === 'manual_transfer'
    ? 'BANK_TRANSFER'
    : state.paymentMethod;

  const orderData = {
    customerInfo: {
      firstName: (state.fullName || '').split(' ')[0] || state.fullName,
      lastName: (state.fullName || '').split(' ').slice(1).join(' ') || '',
      email: state.email,
      phone: state.whatsappNumber,
      address: state.deliveryAddress,
      state: state.deliveryState,
      city: state.deliveryCity || '',
      country: state.deliveryCountry || 'Nigeria'
    },
    shippingInfo: hasPhysicalProducts && state.fulfillmentType !== 'PICKUP' ? {
      firstName: (state.fullName || '').split(' ')[0] || state.fullName,
      lastName: (state.fullName || '').split(' ').slice(1).join(' ') || '',
      address: state.deliveryAddress,
      state: state.deliveryState,
      city: state.deliveryCity || '',
      country: state.deliveryCountry || 'Nigeria',
      phone: state.whatsappNumber
    } : null,
    items: cartItems.map(({ product, qty, variations, variantSku, sellByUnit, unitOfMeasure, unitQuantity, pricePerUnit }) => ({
      productId: product._id || product.id,
      name: product.name,
      price: product.price,
      qty,
      quantity: qty,
      // Variant fields
      variations: variations || product.variations || null,
      variantId: product.variantId || null,
      variantSku: variantSku || product.variantSku || null,
      variantName: product.variationText || null,
      variantCombination: variations || product.variations || null,
      // Unit-based selling fields
      unitOfMeasure: unitOfMeasure || product.unitOfMeasure || null,
      unitQuantity: unitQuantity || product.unitQuantity || null,
      pricePerUnit: pricePerUnit || product.pricePerUnit || null
    })),
    subtotal: cartTotal,
    shippingFee,
    paymentMethod: backendPaymentMethod,
    storeSlug,
    deliveryMethod: hasPhysicalProducts ? state.deliveryMethod : 'digital',
    fulfillmentType: hasPhysicalProducts ? state.fulfillmentType : null,
    pickupLocationId: state.fulfillmentType === 'PICKUP' ? state.pickupLocationId : null,
    chowdeckFeeId: hasPhysicalProducts && state.deliveryMethod === 'CHOWDECK' ? state.chowdeckFeeId : null,
    // Full Terminal Africa rate object — checkoutPrismaNew persists
    // it on Order.metadata.terminalAfrica so Phase 6 dispatch can
    // book against the same server-signed rate.
    terminalAfrica: hasPhysicalProducts && state.deliveryMethod === 'TERMINAL_AFRICA'
      ? state.terminalAfrica
      : null,
    chowdeckQuoteToken: state.chowdeckQuoteToken || null
  };

  return { orderData, storeSlug, discountCode: state.appliedDiscount?.code };
}

const PaymentStep = ({ state, dispatch, storeData, storeSlug, formatPrice, goToStep, goBack, cartItems, cartTotal, onClose, hasPhysicalProducts = true, modalTheme = 'light' }) => {
  const [error, setError] = useState(null);
  const [quoteState, setQuoteState] = useState(null);
  const paymentInFlight = useRef(false);
  const [quoteError, setQuoteError] = useState(null);
  const reduxDispatch = useReduxDispatch();
  const customerAuth = useCustomerAuth();
  const isCustomerLoggedIn = customerAuth?.isAuthenticated || false;

  // Prefer finalShippingCost — it's the post-free-shipping-threshold
  // number the customer actually owes. Falls back to raw shippingCost
  // for backward compat with older shippingCalculation payloads that
  // don't include finalShippingCost.
  const merchantShippingFee = state.shippingCalculation?.finalShippingCost != null
    ? Number(state.shippingCalculation.finalShippingCost)
    : (state.shippingCalculation?.shippingCost || 0);

  const selectedShippingFee = !hasPhysicalProducts || state.fulfillmentType === 'PICKUP' ? 0
    : state.deliveryMethod === 'CHOWDECK' ? (state.chowdeckDeliveryFee || 0)
      : state.deliveryMethod === 'TERMINAL_AFRICA'
        ? (state.terminalAfricaFee ?? state.terminalAfrica?.feeQuoted ?? 0)
        : merchantShippingFee;
  const checkoutRequestJson = JSON.stringify(buildCheckoutRequest({
    state, storeSlug, cartItems, cartTotal, shippingFee: selectedShippingFee, hasPhysicalProducts,
  }));
  const quoteRequestKey = `${isCustomerLoggedIn}:${checkoutRequestJson}`;
  const quote = quoteState?.request === quoteRequestKey ? quoteState.data : null;
  const quoteLoading = !quote && !quoteError;

  // Resolve product prices, discounts, selected shipping, fees and tax through
  // the very same backend path used to create the order, before enabling Pay.
  useEffect(() => {
    let cancelled = false;
    setQuoteError(null);
    const calculateQuote = async () => {
      try {
        const resp = await apiFetch('/checkout/quote', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: checkoutRequestJson, credentials: 'include',
        });
        const data = await resp.json();
        if (!resp.ok || data.success !== true || data.total == null || !Number.isFinite(Number(data.total))) {
          throw new Error(data.error || 'Unable to calculate your total. Please go back and try again.');
        }
        if (!cancelled) {
          setQuoteState({ request: quoteRequestKey, data });
          dispatch({ type: 'SET_FIELDS', fields: { serviceChargeInfo: data, calculatedTotal: Number(data.total) } });
        }
      } catch (e) {
        if (!cancelled) setQuoteError(e.message);
      }
    };
    calculateQuote();
    return () => { cancelled = true; };
  }, [checkoutRequestJson, quoteRequestKey, dispatch]);

  const shippingFee = quote?.shippingFee ?? selectedShippingFee;
  const discountAmount = quote?.discountAmount ?? 0;
  const serviceCharge = quote?.serviceCharge ?? 0;
  const quoteTax = getQuoteTaxInfo(quote || {});
  const taxAmount = quoteTax.amount;
  const taxRate = quoteTax.rate;
  const taxName = quoteTax.name || 'Tax/VAT';
  const finalTotal = quote?.total;
  const platformFeeQuote = quote?.platformFeeQuote || null;

  const handleSelectPaymentMethod = (method) => {
    dispatch({ type: 'UPDATE_FIELD', field: 'paymentMethod', value: method });
    setError(null);
  };

  const handlePaymentVerification = useCallback(async (reference, orderId, transactionId) => {
    try {
      const resp = await apiFetch('/checkout/verify-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference, orderId, storeSlug, transaction_id: transactionId })
      });
      const data = await resp.json();
      if (data.success) {
        localStorage.removeItem('dl_token');
        reduxDispatch(clearCart());
        dispatch({
          type: 'SET_FIELDS',
          fields: {
            completedOrder: data.order || { id: orderId, orderNumber: data.orderNumber }
          }
        });
        goToStep(STEPS.ORDER_CONFIRMED);
      } else {
        dispatch({ type: 'SET_ERROR', payload: data.message || 'Payment verification failed' });
      }
    } catch (e) {
      dispatch({ type: 'SET_ERROR', payload: 'Payment verification failed' });
    }
    paymentInFlight.current = false;
    dispatch({ type: 'SET_LOADING', payload: false });
  }, [storeSlug, reduxDispatch, dispatch, goToStep]);

  const handlePayment = async () => {
    if (!quote || quoteError || paymentInFlight.current) return;
    if (!state.paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    paymentInFlight.current = true;
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    setError(null);

    try {
      const payload = JSON.parse(checkoutRequestJson);
      payload.orderData.expectedTotal = quote.total;
      payload.orderData.total = quote.total;
      payload.orderData.dl_token = localStorage.getItem('dl_token') || null;
      const endpoint = isCustomerLoggedIn ? '/checkout/checkout' : '/checkout/guest-checkout';

      const resp = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      const data = await resp.json();
      if (data.code === 'CHECKOUT_TOTAL_CHANGED' && data.quote) {
        setQuoteState({ request: quoteRequestKey, data: data.quote });
        dispatch({ type: 'SET_FIELDS', fields: { serviceChargeInfo: data.quote, calculatedTotal: data.quote.total } });
      }
      if (!resp.ok || data.success !== true) {
        throw new Error(data.error || data.message || 'Checkout failed. Please try again.');
      }
      const payment = data?.payment;

      // Extract order for use in payment flows
      const order = data?.order || data;
      const orderId = order?.id || order?._id;

      // COD is an order request, not a payment success. Keep the customer in
      // a distinct awaiting-merchant-confirmation state.
      if (state.paymentMethod === 'cod' && data?.cod) {
        reduxDispatch(clearCart());
        dispatch({ type: 'SET_FIELDS', fields: { completedOrder: order } });
        goToStep(STEPS.ORDER_CONFIRMED);
        return;
      }

      // Manual Bank Transfer branch — backend short-circuits gateway
      // init and returns virtualTerminal details for the order. Save
      // them into modal state and transition to the ManualTransferStep.
      if (state.paymentMethod === 'manual_transfer' && data?.virtualTerminal) {
        dispatch({
          type: 'SET_FIELDS',
          fields: {
            manualTransfer: {
              orderId,
              orderNumber: order?.orderNumber,
              amount: Number(order?.total ?? state.calculatedTotal ?? cartTotal),
              currency: order?.currency || 'NGN',
              virtualTerminal: data.virtualTerminal,
              expiresAt: data.manualTransfer?.expiresAt || null,
            },
          },
        });
        goToStep(STEPS.MANUAL_TRANSFER);
        return;
      }

      // Handle Flutterwave inline checkout (stays in modal)
      if (payment?.useInlineCheckout && payment?.provider === 'FLUTTERWAVE') {
        if (!window.FlutterwaveCheckout) {
          throw new Error('Flutterwave script not loaded');
        }
        window.FlutterwaveCheckout({
          public_key: payment.publicKey,
          tx_ref: payment.paymentData.tx_ref,
          amount: payment.paymentData.amount,
          currency: payment.paymentData.currency || 'NGN',
          payment_options: payment.paymentData.payment_options || 'card,banktransfer,ussd',
          customer: payment.paymentData.customer,
          customizations: payment.paymentData.customizations,
          meta: payment.paymentData.meta,
          callback: (response) => {
            if (response.status === 'successful' || response.status === 'completed') {
              // Verify payment inline — pass transaction_id for Flutterwave verification
              handlePaymentVerification(
                response.tx_ref || payment.paymentData.tx_ref,
                orderId,
                response.transaction_id
              );
            } else {
              paymentInFlight.current = false;
              dispatch({ type: 'SET_ERROR', payload: 'Payment was not successful' });
              dispatch({ type: 'SET_LOADING', payload: false });
            }
          },
          onclose: () => {
            paymentInFlight.current = false;
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        });
        return;
      }

      if (state.paymentMethod === 'paystack') {
        if (typeof window.PaystackPop !== 'function' || !payment?.access_code) {
          throw new Error('Unable to open the payment popup. Please refresh and try again.');
        }
        const popup = new window.PaystackPop();
        popup.resumeTransaction(payment.access_code, {
          onSuccess: transaction => handlePaymentVerification(transaction.reference || payment.reference, orderId),
          onCancel: () => {
            paymentInFlight.current = false;
            dispatch({ type: 'SET_LOADING', payload: false });
          },
          onError: () => {
            paymentInFlight.current = false;
            dispatch({ type: 'SET_ERROR', payload: 'Unable to open the payment popup. Please try again.' });
            dispatch({ type: 'SET_LOADING', payload: false });
          },
        });
        return;
      }
      throw new Error('Payment initialization failed. Please try again.');
    } catch (err) {
      paymentInFlight.current = false;
      console.error('Payment error:', err);
      dispatch({ type: 'SET_ERROR', payload: err.message || 'Payment failed. Please try again.' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // If order is already completed, show confirmed state as fallback
  if (state.completedOrder) {
    const isCodOrder = String(state.completedOrder?.paymentMethod || '').toLowerCase() === 'cod';
    return (
      <div className="cm-confirmed">
        <div className="cm-confirmed-icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="#10B981" />
            <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="cm-confirmed-title">{isCodOrder ? 'Order sent to merchant' : 'Order Confirmed!'}</h3>
        <p className="cm-confirmed-order">Order #{state.completedOrder?.orderNumber || 'Processing'}</p>
        <p>{isCodOrder
          ? 'The merchant will confirm your order before arranging delivery. Payment is due on delivery.'
          : 'Thank you for your purchase. You will receive a confirmation email shortly.'}</p>
        <button className="cm-btn-primary" onClick={onClose} style={{ marginTop: 24 }}>
          Continue Shopping
        </button>
      </div>
    );
  }

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'paystack': return 'Paystack';
      case 'flutterwave': return 'Flutterwave';
      case 'manual_transfer': return 'Bank Transfer';
      case 'cod': return 'Pay on delivery';
      default: return method.charAt(0).toUpperCase() + method.slice(1);
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'paystack':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect width="20" height="20" rx="4" fill="#00C3F7" />
            <path d="M5 7h10M5 10h10M5 13h6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        );
      case 'flutterwave':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect width="20" height="20" rx="4" fill="#F5A623" />
            <path d="M6 10c2-3 6-3 8 0M6 10c2 3 6 3 8 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        );
      case 'manual_transfer':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
          </svg>
        );
      case 'cod':
        return (
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3.5 7.5h10v8h-10zM13.5 10.5h3.1l2.2 2.4v2.6h-5.3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="7" cy="17.5" r="1.7" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="17" cy="17.5" r="1.7" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="10.2" cy="6.1" r="3.2" fill="var(--cm-bg)" stroke="currentColor" strokeWidth="1.5" />
            <path d="M11.7 4.8c-.35-.38-.82-.58-1.44-.58-.8 0-1.32.33-1.32.86 0 .57.55.72 1.26.88.72.16 1.4.4 1.4 1.08 0 .61-.51 1.01-1.4 1.01-.66 0-1.2-.22-1.59-.66M10.2 3.55v5.12" stroke="currentColor" strokeWidth="1.05" strokeLinecap="round" />
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="4" width="16" height="12" rx="2" />
            <path d="M2 8h16" />
          </svg>
        );
    }
  };

  // Manual Bank Transfer availability. Rendered as a payment method tile
  // when the merchant has enabled it AND the store currency is NGN
  // (Paystack VT is NGN-only). Hides silently otherwise — non-NGN
  // customers won't understand a VT-specific unavailability note.
  const manualTransferEnabled =
    storeData?.manualTransferEnabled === true
    && (storeData?.currency || 'NGN').toUpperCase() === 'NGN';

  // COD depends on a physical delivery event. If the cart has any digital/
  // service item, hide COD — the backend enforces the same rule.
  const cartIsAllPhysical = Array.isArray(cartItems) && cartItems.length > 0
    && cartItems.every(({ product }) => {
      const type = String(product?.productType || product?.type || '').toUpperCase();
      return !type || type === 'PHYSICAL';
    });

  const combinedPaymentMethods = [
    ...(state.paymentGateways || []),
    ...(manualTransferEnabled ? ['manual_transfer'] : []),
    ...(state.codAvailable === true && cartIsAllPhysical ? ['cod'] : [])
  ];

  return (
    <div className="cm-step-content">
      {/* Order Summary */}
      <div className="cm-order-summary">
        <h4 className="cm-summary-title">Order Summary</h4>

        <div className="cm-summary-items">
          {(quote?.items?.map(item => ({ product: { name: item.name, price: item.price }, qty: item.qty })) || cartItems).map((item, index) => (
            <div key={index} className="cm-summary-item">
              <span className="cm-summary-item-name">
                {item.product.name} x {item.qty}
              </span>
              <span className="cm-summary-item-price">
                {formatPrice(item.product.price * item.qty)}
              </span>
            </div>
          ))}
        </div>

        <div className="cm-summary-divider" />

        <CustomerFacingSubtotal
          subtotal={quote?.subtotal ?? cartTotal}
          processingFee={
            serviceCharge > 0 && String(quote?.whoPays || '').toLowerCase() === 'customer'
              ? serviceCharge
              : 0
          }
          processingFeeLabel={platformFeeQuote ? 'Subtotal incl. transaction fee' : undefined}
          feeBreakdown={platformFeeQuote ? [{ label: 'Transaction fee', amount: serviceCharge }] : null}
          formatCurrency={formatPrice}
          dark={modalTheme === 'dark'}
          compact
          rowStyle={{ fontSize: '14px' }}
          labelStyle={{ color: 'var(--cm-text)', fontWeight: 400 }}
          valueStyle={{ color: 'var(--cm-text)', fontWeight: 400 }}
          subLabel={false}
        />

        {discountAmount > 0 && (
          <div className="cm-summary-row cm-summary-discount">
            <span>Discount ({quote?.discountCode || state.appliedDiscount?.code})</span>
            <span>-{formatPrice(discountAmount)}</span>
          </div>
        )}

        {hasPhysicalProducts ? (
          <div className="cm-summary-row">
            <span>Shipping {
              state.deliveryMethod === 'CHOWDECK'
                ? '(Chowdeck Express)'
                : state.deliveryMethod === 'TERMINAL_AFRICA'
                  ? `(${state.terminalAfrica?.carrierName || 'Nationwide'})`
                  : '(Standard)'
            }</span>
            <span>{shippingFee === 0 ? 'FREE' : formatPrice(shippingFee)}</span>
          </div>
        ) : (
          <div className="cm-summary-row">
            <span>Digital delivery</span>
            <span>FREE</span>
          </div>
        )}

        {taxAmount > 0 && (
          <div className="cm-summary-row">
            <span>{taxName}{taxRate > 0 ? ` (${Number(taxRate).toFixed(1).replace(/\.0$/, '')}%)` : ''}</span>
            <span>{formatPrice(taxAmount)}</span>
          </div>
        )}

        <div className="cm-summary-divider" />

        <div className="cm-summary-row cm-summary-total">
          <span>Total</span>
          {quote
            ? <span>{formatPrice(finalTotal)}</span>
            : quoteError
              ? <span className="cm-summary-total-state">Unavailable</span>
              : <span className="cm-summary-total-state">Calculating...</span>}
        </div>
      </div>

      {/* Payment Methods */}
      <div className="cm-payment-methods">
        <h4 className="cm-payment-methods-title">Payment Method</h4>

        {combinedPaymentMethods && combinedPaymentMethods.length > 0 ? (
          combinedPaymentMethods.map((method) => {
            const methodKey = typeof method === 'string' ? method : method.id || method.name || method.gateway;
            return (
              <div
                key={methodKey}
                className={`cm-payment-method${state.paymentMethod === methodKey ? ' selected' : ''}`}
                onClick={() => handleSelectPaymentMethod(methodKey)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectPaymentMethod(methodKey); }}
              >
                <div className="cm-payment-method-radio">
                  <div className={`cm-radio${state.paymentMethod === methodKey ? ' cm-radio-checked' : ''}`} />
                </div>
                <div className="cm-payment-method-icon">
                  {getPaymentMethodIcon(methodKey)}
                </div>
                <div className="cm-payment-method-label">
                  {getPaymentMethodLabel(methodKey)}
                </div>
              </div>
            );
          })
        ) : (
          <p className="cm-no-methods">No payment methods available.</p>
        )}
      </div>

      {/* Error display */}
      {(error || state.error) && (
        <div className="cm-error">{error || state.error}</div>
      )}

      {quoteError && <p role="alert" className="cm-error">{quoteError}</p>}

      {/* Action buttons */}
      <div className="cm-step-footer">
        <button className="cm-btn-secondary" onClick={goBack} type="button" disabled={state.loading}>
          Back
        </button>
        <button
          className={`cm-btn-primary cm-btn-full${state.paymentMethod === 'cod' ? ' cm-cod-submit' : ''}`}
          onClick={handlePayment}
          type="button"
          disabled={state.loading || !quote || Boolean(quoteError) || !state.paymentMethod}
        >
          {state.loading ? 'Processing...' : quoteLoading ? 'Calculating total...' : quote
            ? state.paymentMethod === 'cod'
              ? `Place order · ${formatPrice(finalTotal)}`
              : state.paymentMethod === 'manual_transfer'
                ? `Continue to bank transfer · ${formatPrice(finalTotal)}`
                : `Pay ${formatPrice(finalTotal)}`
            : 'Pay'}
        </button>
      </div>
    </div>
  );
};

export default PaymentStep;
